import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import * as S from './CategorySelectBox.styles';
import { findSubcategoriesByName } from './CategorySelectBox.helpers';
import {
  SearchInput,
  SearchInputRef,
} from '@/components/SearchInput/SearchInput';
import { CategoriesIcon } from '@/components/UI/Icons/Icons';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { useNewAdContext } from '@/contexts/NewAdContext';
import { Category } from '@/api/types/categories.types';
import { NewAdPageTestIds } from '@/__e2e__/testIds/new-ad-page';

interface CategorySelectBoxProps {
  isOpen?: boolean;
  onSelectSubcategory: (subcategory: Category | null) => void;
}

export const CategorySelectBox: FC<CategorySelectBoxProps> = ({
  isOpen,
  onSelectSubcategory,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const searchInputRef = useRef<SearchInputRef>(null);
  const [query, setQuery] = useState('');
  const {
    categories,
    selectedSubcategory,
    selectedCategory,
    setSelectedCategory,
  } = useNewAdContext();

  useEffect(() => {
    if (isOpen) {
      const elements = document.querySelectorAll('[data-selected="true"]');
      elements.forEach(element => element.scrollIntoView());
      searchInputRef.current?.clear();
    }
  }, [isOpen]);

  const filteredSubcategories = useMemo(
    () =>
      findSubcategoriesByName(query, categories).flatMap(({ data }) => data),
    [query]
  );

  return (
    <S.Container data-testid={NewAdPageTestIds.categorySelect.box}>
      <SearchInput
        ref={searchInputRef}
        onSearch={setQuery}
        hasPadding={false}
        hasBorderBottom={false}
        customPaddingTop={17.5}
        customPaddingBottom={17.5}
        placeholder={t('catalogue.category_search')}
        leftIcon={
          <CategoriesIcon
            fill={theme.palette.tertiary.labels}
            width={20}
            height={20}
          />
        }
      />
      <S.Box>
        {query && filteredSubcategories.length ? (
          <S.CategoryItemsWrapper>
            <S.SubcategoryItems>
              {filteredSubcategories.map(subcategory => (
                <S.SubcategoryFilteredItem
                  dataTestId={NewAdPageTestIds.categorySelect.subcategoryItem}
                  onChangeHook={() => onSelectSubcategory(subcategory)}
                  key={subcategory.id}
                  value={selectedSubcategory?.id === subcategory.id}
                  appearance="radio"
                  size="sm"
                  customContent={
                    <S.SubcategoryCustomContent>
                      {subcategory.parent?.icon && (
                        <img
                          alt=""
                          src={subcategory.parent.icon}
                          width={30}
                          height={30}
                          loading="lazy"
                        />
                      )}
                      <S.SubcategoryCustomInfo>
                        <Typography.Body>{subcategory.name}</Typography.Body>
                        <Typography.Caption $variant="2">
                          {subcategory.parent?.name}
                        </Typography.Caption>
                      </S.SubcategoryCustomInfo>
                    </S.SubcategoryCustomContent>
                  }
                  hasSwitch={false}
                  hasBorderBottom={false}
                />
              ))}
            </S.SubcategoryItems>
          </S.CategoryItemsWrapper>
        ) : (
          <S.CategoryItemsWrapper>
            <S.CategoryItems>
              {categories.map(item => (
                <S.CategoryItem
                  data-testid={NewAdPageTestIds.categorySelect.categoryItem}
                  key={item.id}
                  onMouseEnter={() => setSelectedCategory(item)}
                  $isSelected={selectedCategory?.id === item.id}
                  data-selected={selectedCategory?.id === item.id}
                >
                  <S.CategoryItemIconContainer>
                    <img
                      alt=""
                      src={item.icon}
                      width={30}
                      height={30}
                      loading="lazy"
                    />
                  </S.CategoryItemIconContainer>
                  <S.CategoryLabel>{item.name}</S.CategoryLabel>
                  <S.CategoryChevronIcon />
                </S.CategoryItem>
              ))}
            </S.CategoryItems>
            {selectedCategory?.children && (
              <S.SubcategoryItems>
                {selectedCategory.children.map(subcategory => (
                  <S.SubcategoryItem
                    dataTestId={NewAdPageTestIds.categorySelect.subcategoryItem}
                    onChangeHook={() => onSelectSubcategory(subcategory)}
                    key={subcategory.id}
                    value={selectedSubcategory?.id === subcategory.id}
                    label={subcategory.name}
                    appearance="radio"
                    size="sm"
                    hasBorderBottom={false}
                  />
                ))}
              </S.SubcategoryItems>
            )}
          </S.CategoryItemsWrapper>
        )}
      </S.Box>
    </S.Container>
  );
};

CategorySelectBox.displayName = 'CategorySelectBox';
